import { getSearchIndexArr, type CellMatrix } from "@fortune-sheet/core"

import type { RangeDecoration, WorkbookRange } from "./rangeDecorations"

/**
 * FIND-IN-WORKBOOK — the search half of the one highlight mechanism (Concept
 * "Excel declarations and cell evidence", Part VII · "Search").
 *
 * PURE. No React, no renderer instance, no DOM: a matrix in, rectangles out.
 * The query runs through the library's OWN matcher (`getSearchIndexArr`), which
 * matches on the cell's *displayed* value — the invariant "the displayed value
 * is the asserted value" (Part IX), for free, and the same string the reviewer
 * reads on screen.
 *
 * Two costs are managed here and nowhere else:
 *
 *  - **Coalescing.** The matcher answers in cells. A query that matches a whole
 *    column of a 40k-row BOM is 40k cells and would be 40k divs. Adjacent hits
 *    are merged into maximal rectangles — that column collapses to ONE — so the
 *    DOM cost tracks the *shape* of the match, not its size.
 *  - **Capping.** A checkerboard match coalesces to nothing, so the rendered
 *    rectangles are capped per sheet and the caller is handed `shown` next to
 *    `ranges.length` so it can say "showing first N of M". A capped result is a
 *    visible gap, never a silent truncation (Part IX).
 *
 * Stepping walks CELLS, not rectangles, and is never capped — the counts live in
 * the bar, not in the DOM — and it is ordered `(surface ordinal, row, col)`, the
 * row-major analogue of the PDF track's `(page, y, x)`.
 */

/** A matching cell. 0-based, as the renderer indexes. */
export type CellHit = { row: number; column: number }

/** One sheet, as search sees it: an ordinal, a name, and a dense matrix. */
export type SearchableSheet = {
  /** 1-based surface ordinal — the sort key across sheets. */
  ordinal: number
  /** Sheet name, as the workbook spells it (what a decoration is keyed by). */
  name: string
  /** The renderer's dense cell matrix. Read, never copied. */
  matrix: CellMatrix
}

export type SheetSearch = {
  ordinal: number
  sheet: string
  /** Every matching cell, row-major. The stepping track; never capped. */
  hits: CellHit[]
  /** The hits coalesced into maximal rectangles, row-major. */
  ranges: WorkbookRange[]
  /** How many of `ranges` are painted. Below `ranges.length` ⇒ capped. */
  shown: number
}

/**
 * Rendered rectangles per sheet. Chosen against the measured worst case rather
 * than a feeling: on a 40k-row × 5-column BOM the pathological query (a term in
 * every row of one column) coalesces to a SINGLE rectangle, and the realistic
 * scattered query stays in the low hundreds. 200 keeps the overlay's child count
 * in the same order as the library's own per-sheet overlay elements while
 * leaving every plausible query uncapped; anything worse is a checkerboard,
 * which is exactly the case that should be reported rather than painted.
 */
export const SEARCH_RANGE_CAP = 200

/** Yellow, and only search is yellow — the proof palette has no yellow in it. */
const SEARCH_FILL = "rgba(255, 244, 64, 0.38)"
const SEARCH_ACTIVE_FILL = "rgba(255, 244, 64, 0.85)"
const SEARCH_ACTIVE_BORDER = "rgb(120, 113, 0)"

/** Class hooks for callers that want to style the layer further. */
export const SEARCH_HIT_CLASS = "wb-search-hit"
export const SEARCH_ACTIVE_HIT_CLASS = "wb-search-hit-active"

/**
 * Merge adjacent hits into maximal rectangles.
 *
 * Two passes, both linear in the hits: horizontal runs per row, then vertical
 * extension of runs that repeat the same column span on the next row. That is
 * what collapses a whole-column match (N rows × 1 col → 1 rectangle) and a
 * matched table body (N rows × K adjacent cols → 1 rectangle). A truly scattered
 * match cannot be collapsed and comes back one rectangle per cell — the case the
 * cap exists for.
 */
export function coalesceHits(hits: CellHit[]): WorkbookRange[] {
  const byRow = new Map<number, number[]>()
  for (const hit of hits) {
    const row = byRow.get(hit.row)
    if (row) row.push(hit.column)
    else byRow.set(hit.row, [hit.column])
  }

  const closed: WorkbookRange[] = []
  // Rectangles still growing downward, keyed by their column span.
  let open = new Map<string, WorkbookRange>()
  let previousRow: number | null = null

  for (const row of [...byRow.keys()].sort((a, b) => a - b)) {
    // A gap in rows closes everything: rectangles are contiguous by definition.
    if (previousRow !== null && row !== previousRow + 1) {
      closed.push(...open.values())
      open = new Map()
    }

    const columns = [...new Set(byRow.get(row) ?? [])].sort((a, b) => a - b)
    const runs: WorkbookRange[] = []
    let start = columns[0]
    for (let i = 1; i <= columns.length; i += 1) {
      if (i === columns.length || columns[i] !== columns[i - 1] + 1) {
        runs.push({
          startRow: row,
          endRow: row,
          startColumn: start,
          endColumn: columns[i - 1],
        })
        start = columns[i]
      }
    }

    const next = new Map<string, WorkbookRange>()
    for (const run of runs) {
      const key = `${run.startColumn}:${run.endColumn}`
      const growing = open.get(key)
      if (growing) {
        growing.endRow = row
        next.set(key, growing)
        open.delete(key)
      } else {
        next.set(key, run)
      }
    }
    // Whatever did not repeat on this row is finished.
    closed.push(...open.values())
    open = next
    previousRow = row
  }
  closed.push(...open.values())

  return closed.sort(
    (a, b) => a.startRow - b.startRow || a.startColumn - b.startColumn,
  )
}

/**
 * Run one sheet against every active term — the bar's chips plus its live input,
 * joined as a union, exactly as the PDF viewer treats them. Each term is matched
 * case-insensitively as a substring, the library's default mode and what a
 * reviewer means by ⌘F.
 *
 * The matcher indexes `matrix[r][c]` without guarding the row, so the range
 * handed to it is clamped to the matrix the caller actually holds.
 */
export function searchSheetTerms(
  sheet: SearchableSheet,
  terms: string[],
  cap: number = SEARCH_RANGE_CAP,
): SheetSearch {
  const empty: SheetSearch = {
    ordinal: sheet.ordinal,
    sheet: sheet.name,
    hits: [],
    ranges: [],
    shown: 0,
  }
  const wanted = terms.map((term) => term.trim()).filter(Boolean)
  const rows = sheet.matrix?.length ?? 0
  const columns = rows > 0 ? (sheet.matrix[0]?.length ?? 0) : 0
  if (wanted.length === 0 || rows === 0 || columns === 0) return empty

  const range = [{ row: [0, rows - 1], column: [0, columns - 1] }]
  const seen = new Set<number>()
  const hits: CellHit[] = []
  for (const term of wanted) {
    const found = getSearchIndexArr(term, range, sheet.matrix, {
      regCheck: false,
      wordCheck: false,
      caseCheck: false,
    })
    for (const cell of found as { r: number; c: number }[]) {
      const id = cell.r * columns + cell.c
      if (seen.has(id)) continue
      seen.add(id)
      hits.push({ row: cell.r, column: cell.c })
    }
  }
  // One term comes back row-major already; a union of several does not.
  if (wanted.length > 1) {
    hits.sort((a, b) => a.row - b.row || a.column - b.column)
  }

  const ranges = coalesceHits(hits)
  return {
    ordinal: sheet.ordinal,
    sheet: sheet.name,
    hits,
    ranges,
    shown: Math.min(ranges.length, cap),
  }
}

/** The single-term case. */
export function searchSheet(
  sheet: SearchableSheet,
  query: string,
  cap: number = SEARCH_RANGE_CAP,
): SheetSearch {
  return searchSheetTerms(sheet, [query], cap)
}

/** Matching cells across every evaluated sheet. */
export function totalHits(results: SheetSearch[]): number {
  return results.reduce((sum, r) => sum + r.hits.length, 0)
}

/** Sheets that hold at least one hit — the bar's "in N sheets". */
export function sheetsWithHits(results: SheetSearch[]): number {
  return results.filter((r) => r.hits.length > 0).length
}

/** A cursor into the flattened cross-sheet hit list. */
export type HitCursor = { sheetIndex: number; hitIndex: number }

/**
 * Step the cross-sheet track. `results` must be in surface-ordinal order, which
 * makes the walk `(ordinal, row, col)`. Sheets with no hits are skipped, and the
 * walk wraps at both ends. Answers null exactly when nothing matched anywhere.
 */
export function stepHit(
  results: SheetSearch[],
  cursor: HitCursor | null,
  dir: 1 | -1,
): HitCursor | null {
  const live = results
    .map((r, sheetIndex) => ({ sheetIndex, count: r.hits.length }))
    .filter((s) => s.count > 0)
  if (live.length === 0) return null

  if (cursor === null || (results[cursor.sheetIndex]?.hits.length ?? 0) === 0) {
    const edge = dir === 1 ? live[0] : live[live.length - 1]
    return {
      sheetIndex: edge.sheetIndex,
      hitIndex: dir === 1 ? 0 : edge.count - 1,
    }
  }

  const next = cursor.hitIndex + dir
  const count = results[cursor.sheetIndex].hits.length
  if (next >= 0 && next < count) {
    return { sheetIndex: cursor.sheetIndex, hitIndex: next }
  }

  // Off the end of this sheet — cross into the next live one, wrapping.
  const here = live.findIndex((s) => s.sheetIndex === cursor.sheetIndex)
  const crossed = live[(here + dir + live.length) % live.length]
  return {
    sheetIndex: crossed.sheetIndex,
    hitIndex: dir === 1 ? 0 : crossed.count - 1,
  }
}

/** The cursor's cell, with the sheet it lives on. */
export function hitAt(
  results: SheetSearch[],
  cursor: HitCursor | null,
): { sheet: string; ordinal: number; hit: CellHit } | null {
  if (!cursor) return null
  const result = results[cursor.sheetIndex]
  const hit = result?.hits[cursor.hitIndex]
  if (!hit) return null
  return { sheet: result.sheet, ordinal: result.ordinal, hit }
}

/** The cursor's 1-based position in the flattened track — the bar's `k` of N. */
export function hitPosition(
  results: SheetSearch[],
  cursor: HitCursor | null,
): number {
  if (!cursor) return 0
  let before = 0
  for (let i = 0; i < cursor.sheetIndex; i += 1) {
    before += results[i]?.hits.length ?? 0
  }
  return before + cursor.hitIndex + 1
}

/**
 * The search layer for ONE sheet — the caller composes it with the proof layer
 * and hands the whole thing over (the decoration layer has set semantics).
 *
 * The capped tail is dropped, EXCEPT that the active hit always gets its own
 * block appended last: stepping past the cap must still show the reviewer where
 * they are, and being last puts it over any rectangle containing it.
 */
export function searchDecorations(
  result: SheetSearch | null | undefined,
  activeHit: CellHit | null,
): RangeDecoration[] {
  if (!result) return []
  const decorations: RangeDecoration[] = result.ranges
    .slice(0, result.shown)
    .map((range) => ({
      id: `wb-hit:${result.sheet}:${range.startRow}:${range.startColumn}:${range.endRow}:${range.endColumn}`,
      sheet: result.sheet,
      range,
      fill: SEARCH_FILL,
      className: SEARCH_HIT_CLASS,
    }))
  if (activeHit) {
    decorations.push({
      id: `wb-hit-active:${result.sheet}:${activeHit.row}:${activeHit.column}`,
      sheet: result.sheet,
      range: {
        startRow: activeHit.row,
        endRow: activeHit.row,
        startColumn: activeHit.column,
        endColumn: activeHit.column,
      },
      fill: SEARCH_ACTIVE_FILL,
      border: SEARCH_ACTIVE_BORDER,
      className: SEARCH_ACTIVE_HIT_CLASS,
    })
  }
  return decorations
}
